import { ToastContext } from "context/ToastContext";
import { Toast } from "primereact/toast";
import {
  ReactNode,
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import { createPortal } from "react-dom";
import { tourSelector } from "training/anchors";
import { ModuleTitleScreen } from "training/components/ModuleTitleScreen";
import { TourPopover } from "training/components/TourPopover";
import { CURRICULUM } from "training/curriculum";
import { CURRICULUM_VERSION } from "training/curriculum/version";
import {
  firstUnpassedModule,
  moduleQuizPath,
  passedModules,
  QUIZ_PATH,
} from "training/quizHelpers";
import {
  ITourContext,
  ITourOrder,
  ITourStep,
  TourModule,
} from "training/types";
import { IMyTrainingRecord, ITrainingProgress } from "types/Training";

import { moduleSegmentAround, opensModule, tourModuleOf } from "./modules";
import { isTourDone, nextVisibleFrom, resolveStart } from "./progress";
import {
  getTourSession,
  ITourHandoff,
  tourEventName,
  updateTourSession,
} from "./session";
import {
  advanceKind,
  buildVisibleSteps,
  DONE_DEBOUNCE_MS,
  DONE_POLL_MS,
  isStepDone,
  POPOVER_CLASS,
  TOUR_PANELS_CLASS,
  routeUrl,
  segmentWaits,
  stepPosition,
  toDriveStep,
} from "./steps";
import { trackTraining, TrainingTelemetryEvent } from "./telemetry";
import { DriveStep, DriverHook, useDriverTour } from "./useDriverTour";

/**
 * CLE-3015 — the tour engine. The one hook that drives driver.js (through useDriverTour); everything
 * else in the app talks to the tour through this hook's `start` / `exit` or through `session.ts`.
 *
 * One driver instance drives one SEGMENT: the run of consecutive steps that live on the current route,
 * within one module (`moduleSegmentAround`).
 *
 * Modules (John's review): the tour runs one module at a time.
 * - Before a module's first step in a run (`opensModule`) the engine shows that module's title screen
 *   (ModuleTitleScreen, an opaque full-page card built from MODULES); "Start Module N" drives its steps.
 * - After a module's last step the run ends: `currentStepId` moves to the next module's first step, the
 *   progress is saved, and the trainee is sent to the module's quiz (/training/module/N/quiz). Passing it
 *   records the pass on the server; its "Start Module N+1" reopens the tour, which resumes at that step and
 *   shows that module's title screen. A module already passed (a re-run) carries straight on to the next
 *   title screen instead.
 * - The engine never shows a step of module N while an earlier module's quiz is unpassed: a start, a resume
 *   or a hand-off that lands there goes to that quiz instead. After the last module's last step it goes to
 *   the first unpassed module's quiz, or, with all six passed, to the knowledge check.
 * Which modules are passed: the saved record's `modules` plus this session's passes (`passedModules`).
 *
 * When the next step lives on another route the engine hands off: it saves `currentStepId`, leaves a
 * hand-off in the session, destroys the driver, and either lets the clicked element navigate (an
 * `advanceOn: "route"` or `"event"` step) or navigates itself; the host on the new page picks the
 * hand-off up and drives from there.
 *
 * The engine keeps its own record of what happened rather than trusting driver.js's end callback:
 * driver.js skips a missing target silently, and when every remaining target is missing it never fires
 * `onDestroyed` at all. So every highlight is compared with the index the engine expected; the steps in
 * between were skipped (`target-missing`, or `not-shown` for an optional step) and are recorded, and
 * running off the end is caught in `onDestroyStarted("done")`.
 *
 * Progress is written on every change, debounced (500 ms), and flushed at once on a hand-off, an exit,
 * the end, and unmount. Any exception in here tears the tour down and is logged; it never reaches the page.
 */
export const SAVE_DEBOUNCE_MS = 500;
/** A step shown this long without the trainee moving on counts as stuck (recorded once per step). */
export const STUCK_AFTER_MS = 120_000;
/**
 * Longer for steps where the trainee has real work to do — typing a form (`fill`) or getting the form to a
 * state Create and Send accepts (`event`) — so a normal run does not read as stuck.
 */
export const STUCK_AFTER_MS_WORK = 300_000;

/** The toast Exit shows (the banner's Resume lives on the Estimates tab). */
export const EXIT_TOAST_TEXT =
  "Your progress is saved. Resume any time from the Estimates tab.";

export const stuckAfterMs = (step: ITourStep) =>
  step.mode === "fill" || advanceKind(step) === "event"
    ? STUCK_AFTER_MS_WORK
    : STUCK_AFTER_MS;

export interface IUseTourOptions {
  context: ITourContext;
  /** GET /user/me/training. `start` does nothing until it has loaded. */
  record: IMyTrainingRecord | undefined;
  onSave: (progress: ITrainingProgress) => unknown;
  navigate: (to: string) => void;
  /** Injectable for tests; the real curriculum by default. */
  steps?: ITourStep[];
}

export interface IUseTourResult {
  start: (options?: { handoff?: ITourHandoff }) => void;
  exit: () => void;
  isActive: boolean;
  currentStep: ITourStep | null;
  /** The popover content, portalled into driver.js's popover; render it anywhere. */
  popover: ReactNode;
}

const clone = (progress: ITrainingProgress): ITrainingProgress =>
  JSON.parse(JSON.stringify(progress));

export function useTour({
  context,
  record,
  onSave,
  navigate,
  steps = CURRICULUM,
}: IUseTourOptions): IUseTourResult {
  const [run, setRun] = useState<{
    steps: DriveStep[];
    startIndex: number;
    key: number;
  } | null>(null);
  const [currentStep, setCurrentStep] = useState<ITourStep | null>(null);
  const [activeIndex, setActiveIndex] = useState<number | undefined>();
  /** The current step's gate (`done`) is open: Next is enabled. Always true for a step without a gate. */
  const [canAdvance, setCanAdvance] = useState(true);
  /** The module title screen on show, and the step its "Start Module N" drives from. */
  const [title, setTitle] = useState<{
    module: TourModule;
    stepId: string;
  } | null>(null);

  // Everything the driver callbacks read lives in refs: driver.js holds on to the callbacks it was
  // created with, and a stale closure would act on the previous segment.
  const contextRef = useRef(context);
  contextRef.current = context;
  // The app's toast (null outside its provider, as in some tests): Exit says where to resume.
  const toastRef = useContext(ToastContext) as RefObject<Toast> | null;
  const recordRef = useRef(record);
  recordRef.current = record;
  const onSaveRef = useRef(onSave);
  onSaveRef.current = onSave;
  const navigateRef = useRef(navigate);
  navigateRef.current = navigate;
  const curriculumRef = useRef(steps);
  curriculumRef.current = steps;

  const visibleRef = useRef<ITourStep[]>([]);
  const segmentRef = useRef<ITourStep[]>([]);
  const progressRef = useRef<ITrainingProgress | null>(null);
  const expectedRef = useRef(0);
  const activeRef = useRef<number | undefined>(undefined);
  const runningRef = useRef(false);
  const runKeyRef = useRef(0);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const stuckTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const detachEventRef = useRef<(() => void) | null>(null);
  const detachGateRef = useRef<(() => void) | null>(null);
  const modulesDoneRef = useRef(new Set<number>());
  /** Modules whose title screen the trainee has started from in this hook's life: never shown twice. */
  const titlesStartedRef = useRef(new Set<number>());
  const failRef = useRef<(error: unknown) => void>(() => {});
  const driveFromRef = useRef<(stepId: string) => void>(() => {});
  /** The context this run's step list was built with (incl. the trainee's estimate), for its URLs. */
  const runContextRef = useRef<ITourContext>(context);

  const driverApiRef = useRef<ReturnType<typeof useDriverTour> | null>(null);

  const track = (
    event: TrainingTelemetryEvent,
    step?: ITourStep,
    extra?: { reason?: string; resumed?: boolean }
  ) =>
    trackTraining(event, {
      accountId: contextRef.current.accountId,
      curriculumVersion: progressRef.current?.version ?? CURRICULUM_VERSION,
      ...(step ? { stepId: step.id, module: step.module } : {}),
      ...extra,
    });

  // --- progress -----------------------------------------------------------------------------------

  const saveNow = () => {
    if (saveTimerRef.current) {
      clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
    const progress = progressRef.current;
    if (!progress) return;
    const snapshot = clone(progress);
    updateTourSession({ progress: snapshot });
    try {
      Promise.resolve(onSaveRef.current(snapshot)).catch((error) =>
        console.error("[Clear Certified] saving progress failed", error)
      );
    } catch (error) {
      console.error("[Clear Certified] saving progress failed", error);
    }
  };

  const scheduleSave = () => {
    if (progressRef.current) {
      updateTourSession({ progress: clone(progressRef.current) });
    }
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(saveNow, SAVE_DEBOUNCE_MS);
  };

  const setCurrentStepId = (stepId: string | null) => {
    if (progressRef.current) progressRef.current.currentStepId = stepId;
  };

  /** Modules whose quiz is passed: the saved record's, this session's, and this run's copy of the progress. */
  const passedNow = (): Set<number> => {
    const rec = recordRef.current;
    const passed = passedModules(rec);
    const progress = progressRef.current;
    if (rec && progress) {
      passedModules(
        {
          curriculumVersion: rec.curriculumVersion,
          trainingProgress: progress,
        },
        []
      ).forEach((module) => passed.add(module));
    }
    return passed;
  };

  const noteModuleDone = (step: ITourStep) => {
    const inModule = visibleRef.current.filter((s) => s.module === step.module);
    const last = inModule[inModule.length - 1];
    if (last?.id === step.id && !modulesDoneRef.current.has(step.module)) {
      modulesDoneRef.current.add(step.module);
      track("training_module_completed", step);
    }
  };

  const markCompleted = (step: ITourStep) => {
    const progress = progressRef.current;
    if (progress && !progress.completed.includes(step.id)) {
      progress.completed.push(step.id);
    }
    noteModuleDone(step);
  };

  const recordSkip = (step: ITourStep) => {
    const progress = progressRef.current;
    const reason = step.optional ? "not-shown" : "target-missing";
    if (progress && !progress.skipped.some((s) => s.stepId === step.id)) {
      progress.skipped.push({
        stepId: step.id,
        reason,
        at: new Date().toISOString(),
      });
    }
    track("training_step_skipped", step, { reason });
    noteModuleDone(step);
  };

  // --- per-step timers and listeners --------------------------------------------------------------

  /**
   * The lists PrimeReact portals to <body> (Dropdown, AutoComplete, Calendar) take mouse clicks, above the
   * overlay, only while a fill step shows (TOUR_PANELS_CLASS; the rules are in TourPopoverGlobalStyle).
   */
  const setPanelsClickable = (on: boolean) => {
    document.body.classList.toggle(TOUR_PANELS_CLASS, on);
  };

  const clearStepWatchers = () => {
    if (stuckTimerRef.current) {
      clearTimeout(stuckTimerRef.current);
      stuckTimerRef.current = null;
    }
    detachEventRef.current?.();
    detachEventRef.current = null;
    detachGateRef.current?.();
    detachGateRef.current = null;
    setPanelsClickable(false);
  };

  /** Drop a hand-off to THIS page that a run here has picked up (see `start`). Never one to another page. */
  const clearPendingHandoff = () => {
    const { handoff } = getTourSession();
    if (
      handoff &&
      handoff.route === contextRef.current.route &&
      handoff.accountId === contextRef.current.accountId
    ) {
      updateTourSession({ handoff: null });
    }
  };

  // --- teardown -----------------------------------------------------------------------------------

  const teardown = () => {
    clearStepWatchers();
    runningRef.current = false;
    activeRef.current = undefined;
    driverApiRef.current?.destroy();
    setRun(null);
    setCurrentStep(null);
    setActiveIndex(undefined);
    setTitle(null);
    updateTourSession({ running: false, route: null });
    // Belt and braces: never leave a tour popover behind if driver.js rendered one after its own reset.
    setTimeout(() => {
      // The session, not this hook's ref: the host on the next page may already be driving.
      if (getTourSession().running) return;
      document
        .querySelectorAll(`.driver-popover.${POPOVER_CLASS}`)
        .forEach((orphan) => orphan.remove());
    }, 0);
  };

  /** The quiz that must be passed before `step` can show: the lowest unpassed module before its own. */
  const quizBefore = (step: ITourStep): TourModule | null =>
    firstUnpassedModule(passedNow(), tourModuleOf(step));

  /**
   * The run stops at a module quiz: the place is kept (`resumeStepId`, the step the tour carries on from once
   * the quiz is passed), saved at once, and the trainee is sent to the quiz.
   */
  const leaveToQuiz = (module: TourModule, resumeStepId: string | null) => {
    setCurrentStepId(resumeStepId);
    saveNow();
    updateTourSession({ handoff: null });
    teardown();
    navigateRef.current(moduleQuizPath(module));
  };

  /**
   * The tour is over: every step of the run is behind the trainee. What is left is a quiz — the first module
   * quiz not yet passed, or with all six passed the knowledge check (the last card's `onFinishNavigateTo`,
   * else QUIZ_PATH). A run that was already over before it started (no step to show) goes nowhere.
   */
  const finish = (endedOn?: ITourStep, { toQuiz = true } = {}) => {
    setCurrentStepId(null);
    saveNow();
    updateTourSession({ handoff: null });
    teardown();
    if (!toQuiz) return;
    const unpassed = firstUnpassedModule(passedNow());
    if (unpassed !== null) {
      navigateRef.current(moduleQuizPath(unpassed));
      return;
    }
    navigateRef.current(endedOn?.onFinishNavigateTo ?? QUIZ_PATH);
  };

  const handOff = (target: ITourStep, pageNavigatesItself: boolean) => {
    let next = target;
    let url: string | null = null;
    if (!pageNavigatesItself) {
      // A page the engine cannot open (the patient page, entered only through the Collect Payment click
      // that was just skipped): its steps are skipped too, up to the first step whose page can be opened.
      const visible = visibleRef.current;
      let index = visible.indexOf(next);
      url = routeUrl(next.route, runContextRef.current);
      while (url === null && next.route !== contextRef.current.route) {
        recordSkip(next);
        index += 1;
        if (index >= visible.length) {
          finish();
          return;
        }
        next = visible[index];
        url = routeUrl(next.route, runContextRef.current);
      }
      if (next.route === contextRef.current.route) {
        teardown();
        driveFromRef.current(next.id);
        return;
      }
    }
    // Skipping a page the engine cannot open may have carried the tour into a module whose quiz is due.
    const due = quizBefore(next);
    if (due !== null && !pageNavigatesItself) {
      leaveToQuiz(due, next.id);
      return;
    }
    setCurrentStepId(next.id);
    saveNow();
    const progress = progressRef.current;
    if (progress) {
      updateTourSession({
        handoff: {
          accountId: contextRef.current.accountId,
          route: next.route,
          stepId: next.id,
          progress: clone(progress),
        },
      });
    }
    teardown();
    if (url) navigateRef.current(url);
  };

  /** The segment is done (advanced past its last step, or driver.js ran off its end). */
  const leaveSegment = (lastShown?: ITourStep) => {
    const segment = segmentRef.current;
    const visible = visibleRef.current;
    const last = segment[segment.length - 1];
    const lastIndex = visible.indexOf(last);
    const next = visible[lastIndex + 1];
    if (!next) {
      finish(lastShown);
      return;
    }
    // The end of a module: its quiz comes next, unless it is already passed (a re-run), in which case the
    // next module's title screen does.
    const module = tourModuleOf(last);
    if (tourModuleOf(next) !== module && !passedNow().has(module)) {
      leaveToQuiz(module, next.id);
      return;
    }
    const pageNavigatesItself =
      lastShown?.advanceOn === "route" || lastShown?.advanceOn === "event";
    handOff(next, pageNavigatesItself);
  };

  // --- moving through the segment -----------------------------------------------------------------

  const advance = () => {
    const index = activeRef.current;
    if (index === undefined) return;
    const segment = segmentRef.current;
    const step = segment[index];
    clearStepWatchers();
    markCompleted(step);
    if (index + 1 < segment.length) {
      expectedRef.current = index + 1;
      activeRef.current = undefined;
      scheduleSave();
      driverApiRef.current?.moveTo(index + 1);
      return;
    }
    leaveSegment(step);
  };

  /**
   * The nearest earlier step of this segment that can show now: a centred step, or one whose target is on
   * the page. Back goes straight there, so it never waits on (or stalls at) a step whose target is gone.
   */
  const previousShowable = (index: number): number | undefined => {
    const segment = segmentRef.current;
    for (let i = index - 1; i >= 0; i -= 1) {
      const { anchor } = segment[i];
      if (!anchor || document.querySelector(tourSelector(anchor))) return i;
    }
    return undefined;
  };

  const back = () => {
    const index = activeRef.current;
    if (index === undefined) return;
    const target = previousShowable(index);
    if (target === undefined) return;
    clearStepWatchers();
    expectedRef.current = target;
    activeRef.current = undefined;
    driverApiRef.current?.moveTo(target);
  };

  const watchStep = (step: ITourStep) => {
    stuckTimerRef.current = setTimeout(() => {
      try {
        const progress = progressRef.current;
        if (!progress || progress.currentStepId !== step.id) return;
        const stuck = progress.stuck ?? [];
        if (stuck.some((s) => s.stepId === step.id)) return;
        progress.stuck = [
          ...stuck,
          {
            stepId: step.id,
            reason: "no-advance",
            at: new Date().toISOString(),
          },
        ];
        track("training_step_stuck", step, { reason: "no-advance" });
        scheduleSave();
      } catch (error) {
        failRef.current(error);
      }
    }, stuckAfterMs(step));

    if (advanceKind(step) === "event" && step.event) {
      const name = tourEventName(step.event);
      const onEvent = (event: Event) => {
        try {
          const order = (event as CustomEvent<ITourOrder | undefined>).detail;
          const progress = progressRef.current;
          if (order?.id && progress) {
            progress.orderId = order.id;
            if (order.status === "NoBalanceDue") progress.noBalanceDue = true;
            updateTourSession({ order });
          }
          advance();
        } catch (error) {
          failRef.current(error);
        }
      };
      window.addEventListener(name, onEvent);
      detachEventRef.current = () => window.removeEventListener(name, onEvent);
    }

    // Every click step advances from the engine's own CAPTURE listener on `document`, which runs before the
    // clicked element's handlers — never from driver.js's `advanceOnClick` (off in `toDriveStep`), which
    // listens on `document` in the BUBBLE phase. Two kinds of page broke that:
    // - an element that navigates by itself (New Estimate, View, Collect Payment): React renders the
    //   navigation in a microtask that runs before the click bubbles up to `document`, by which time this
    //   page, its host and the driver instance are gone and no hand-off is written (dev feedback round 1);
    // - a handler that calls `stopPropagation()` (the "..." menu's items, RowActionsMenu): the click never
    //   reaches `document`, the step never advanced, and its target unmounted with the menu, leaving a dead
    //   popover over the dialog the click opened (dev QA of #1438: Duplicate Estimate).
    // A capture listener on `document` runs before React's own listeners on the app root.
    if (advanceKind(step) === "click" && step.anchor) {
      const selector = tourSelector(step.anchor);
      const onClick = (event: MouseEvent) => {
        try {
          const target = event.target as Element | null;
          if (target?.closest?.(selector)) advance();
        } catch (error) {
          failRef.current(error);
        }
      };
      document.addEventListener("click", onClick, true);
      detachEventRef.current = () =>
        document.removeEventListener("click", onClick, true);
    }
  };

  /**
   * A gated `fill` step (`done`): Next stays disabled until the step's check holds. The check re-runs, in
   * one batch at most every DONE_DEBOUNCE_MS, after any input / change / click on the page (capture phase,
   * like the click listener above), any DOM change inside the highlighted element, and every DONE_POLL_MS
   * (a value the page sets itself fires neither). Everything is removed when the step changes.
   */
  const watchGate = (step: ITourStep) => {
    if (!step.done || !step.anchor) {
      setCanAdvance(true);
      return;
    }
    const check = () => setCanAdvance(isStepDone(step));
    check();
    let timer: ReturnType<typeof setTimeout> | null = null;
    const schedule = () => {
      if (timer) return;
      timer = setTimeout(() => {
        timer = null;
        try {
          check();
        } catch (error) {
          failRef.current(error);
        }
      }, DONE_DEBOUNCE_MS);
    };
    const events = ["input", "change", "click"];
    events.forEach((name) => document.addEventListener(name, schedule, true));
    const target = document.querySelector(tourSelector(step.anchor));
    const observer = target ? new MutationObserver(schedule) : null;
    if (target && observer) {
      observer.observe(target, {
        subtree: true,
        childList: true,
        attributes: true,
        characterData: true,
      });
    }
    const poll = setInterval(schedule, DONE_POLL_MS);
    detachGateRef.current = () => {
      events.forEach((name) =>
        document.removeEventListener(name, schedule, true)
      );
      observer?.disconnect();
      clearInterval(poll);
      if (timer) clearTimeout(timer);
    };
  };

  // --- driver.js hooks ----------------------------------------------------------------------------

  const onHighlightStarted: DriverHook = (_element, _driveStep, opts) => {
    try {
      const index = opts.driver.getActiveIndex();
      if (index === undefined) return;
      const segment = segmentRef.current;
      for (let i = expectedRef.current; i < index; i += 1) {
        recordSkip(segment[i]);
      }
      expectedRef.current = index;
      activeRef.current = index;
      const step = segment[index];
      // This page is showing the tour: a hand-off that brought it here is used up.
      clearPendingHandoff();
      clearStepWatchers();
      setCurrentStepId(step.id);
      scheduleSave();
      setCurrentStep(step);
      setActiveIndex(index);
      track("training_step_shown", step);
      watchStep(step);
      watchGate(step);
      setPanelsClickable(step.mode === "fill");
    } catch (error) {
      // Not synchronously: driver.js renders this step's popover after this hook returns, and a popover
      // rendered after a destroy would be left on the page with nothing to remove it.
      setTimeout(() => failRef.current(error), 0);
    }
  };

  const onNextClick: DriverHook = () => {
    try {
      advance();
    } catch (error) {
      failRef.current(error);
    }
  };

  const onDestroyStarted: DriverHook = (_element, _driveStep, opts) => {
    try {
      if (opts.reason === "done") {
        // driver.js ran off the end: every step from the expected one on had no target.
        const segment = segmentRef.current;
        for (let i = expectedRef.current; i < segment.length; i += 1) {
          recordSkip(segment[i]);
        }
        leaveSegment();
        return;
      }
      saveNow();
      teardown();
    } catch (error) {
      failRef.current(error);
    }
  };

  const driverApi = useDriverTour({
    steps: run?.steps ?? [],
    enabled: !!run,
    startIndex: run?.startIndex ?? 0,
    runKey: run?.key,
    popoverClass: POPOVER_CLASS,
    hooks: { onHighlightStarted, onNextClick, onDestroyStarted },
  });
  driverApiRef.current = driverApi;

  // --- start / exit -------------------------------------------------------------------------------

  failRef.current = (error: unknown) => {
    console.error("[Clear Certified] the tour stopped after an error", error);
    try {
      // A pending hand-off to this page would restart the failed run on the next mount; Resume does that.
      clearPendingHandoff();
      saveNow();
    } catch {
      // ignore
    }
    try {
      teardown();
    } catch {
      // ignore
    }
  };

  const driveFrom = (
    stepId: string,
    { keepHandoff = false }: { keepHandoff?: boolean } = {}
  ) => {
    const visible = visibleRef.current;
    const index = visible.findIndex((step) => step.id === stepId);
    if (index === -1) {
      finish(undefined, { toQuiz: false });
      return;
    }
    const step = visible[index];
    // Never a step of module N while an earlier module's quiz is unpassed (a resume after the module's
    // steps, a link, a hand-off): that quiz first, and the tour carries on from here once it is passed.
    const due = quizBefore(step);
    if (due !== null) {
      leaveToQuiz(due, step.id);
      return;
    }
    if (step.route !== contextRef.current.route) {
      handOff(step, false);
      return;
    }
    const module = tourModuleOf(step);
    if (opensModule(visible, index) && !titlesStartedRef.current.has(module)) {
      // The module's title screen first; its "Start Module N" comes back here (startModule).
      runningRef.current = true;
      setCurrentStepId(step.id);
      scheduleSave();
      updateTourSession({
        running: true,
        route: contextRef.current.route,
        ...(keepHandoff ? {} : { handoff: null }),
      });
      setTitle({ module, stepId: step.id });
      return;
    }
    const { start, end } = moduleSegmentAround(visible, index);
    const segment = visible.slice(start, end + 1);
    segmentRef.current = segment;
    expectedRef.current = index - start;
    activeRef.current = undefined;
    runningRef.current = true;
    setCurrentStepId(step.id);
    scheduleSave();
    updateTourSession({
      running: true,
      route: contextRef.current.route,
      ...(keepHandoff ? {} : { handoff: null }),
    });
    runKeyRef.current += 1;
    const waits = segmentWaits(segment, index - start);
    setRun({
      steps: segment.map((s, i) => toDriveStep(s, { waitForTarget: waits[i] })),
      startIndex: index - start,
      key: runKeyRef.current,
    });
  };
  driveFromRef.current = driveFrom;

  /** The title screen's "Start Module N": drive the module from the step the screen stood in front of. */
  const startModule = () => {
    try {
      if (!title) return;
      titlesStartedRef.current.add(title.module);
      setTitle(null);
      // A hand-off that brought the tour to this page is cleared once this page's first step highlights.
      driveFrom(title.stepId, { keepHandoff: true });
    } catch (error) {
      failRef.current(error);
    }
  };

  const start = useCallback((options?: { handoff?: ITourHandoff }) => {
    try {
      if (runningRef.current) return;
      const session = getTourSession();
      const baseContext = contextRef.current;
      const handoff = options?.handoff;

      if (handoff) {
        const ctx: ITourContext = {
          ...baseContext,
          order: baseContext.order ?? session.order ?? undefined,
          noBalanceDue: !!handoff.progress.noBalanceDue,
        };
        runContextRef.current = ctx;
        visibleRef.current = buildVisibleSteps(curriculumRef.current, ctx);
        progressRef.current = clone(handoff.progress);
        // The next page may build a different list (its own context); carry on at the handed-off step,
        // or at the first step of this run after it.
        const target = nextVisibleFrom(
          curriculumRef.current,
          visibleRef.current,
          handoff.stepId
        );
        if (!target) {
          finish();
          return;
        }
        // The hand-off stays in the session until this page's first step has been highlighted
        // (onHighlightStarted): a host that unmounts before that (the page re-mounts it, or this run fails
        // to show anything) leaves it for the host that mounts next, which picks it up and carries on.
        driveFrom(target.id, { keepHandoff: true });
        return;
      }

      const rec = recordRef.current;
      if (!rec) return;
      if (rec.curriculumVersion !== CURRICULUM_VERSION) {
        console.warn(
          `[Clear Certified] the API's curriculum version (${rec.curriculumVersion}) differs from this app's (${CURRICULUM_VERSION}); the API's is used`
        );
      }
      // The session's copy is newer than the cached GET while a save is in flight.
      const saved =
        session.progress?.version === rec.curriculumVersion
          ? session.progress
          : rec.trainingProgress;
      const ctx: ITourContext = {
        ...baseContext,
        order: baseContext.order ?? session.order ?? undefined,
        noBalanceDue: !!saved?.noBalanceDue,
      };
      runContextRef.current = ctx;
      const visible = buildVisibleSteps(curriculumRef.current, ctx);
      if (!visible.length) return;
      visibleRef.current = visible;
      // A fresh start (Start, Resume, ?training=1) opens with the title screen of the module it lands in.
      titlesStartedRef.current = new Set();
      // Every step is behind them and a module quiz is not passed (the tour ended, then a reload): that quiz,
      // not a restart from step 1.
      if (
        isTourDone({ saved, serverVersion: rec.curriculumVersion, visible })
      ) {
        const unpassed = firstUnpassedModule(passedModules(rec));
        if (unpassed !== null) {
          navigateRef.current(moduleQuizPath(unpassed));
          return;
        }
      }
      const resolved = resolveStart({
        saved,
        serverVersion: rec.curriculumVersion,
        curriculum: curriculumRef.current,
        visible,
      });
      progressRef.current = resolved.progress;
      modulesDoneRef.current = new Set();
      track(
        "training_started",
        visible.find((s) => s.id === resolved.startStepId),
        { resumed: resolved.resumed }
      );
      driveFrom(resolved.startStepId);
    } catch (error) {
      failRef.current(error);
    }
  }, []);

  const exit = useCallback(() => {
    try {
      if (!runningRef.current) return;
      const index = activeRef.current;
      const step = index === undefined ? undefined : segmentRef.current[index];
      track("training_exited", step);
      saveNow();
      updateTourSession({ handoff: null });
      teardown();
      // Manual pass 2026-10-26: Exit on Estimate Details closed the tour with no word, and that page has no
      // banner to resume from.
      toastRef?.current?.show({
        severity: "info",
        summary: "Training paused",
        detail: EXIT_TOAST_TEXT,
      });
    } catch (error) {
      failRef.current(error);
    }
  }, [toastRef]);

  // Unmounting mid-segment (browser back, a link outside the tour): keep the place, stop the driver.
  useEffect(
    () => () => {
      if (saveTimerRef.current || runningRef.current) {
        try {
          clearStepWatchers();
          saveNow();
        } catch {
          // ignore
        }
      }
      if (runningRef.current) {
        runningRef.current = false;
        updateTourSession({ running: false, route: null });
      }
    },
    []
  );

  const position = currentStep
    ? stepPosition(visibleRef.current, currentStep.id, {
        shown: progressRef.current?.completed,
        skipped: progressRef.current?.skipped.map((s) => s.stepId),
      })
    : null;

  const titleScreen = title
    ? createPortal(
        <ModuleTitleScreen
          module={title.module}
          onStart={startModule}
          onExit={exit}
        />,
        document.body
      )
    : null;

  const popoverNode =
    driverApi.popoverContainer && currentStep
      ? createPortal(
          <TourPopover
            step={currentStep}
            position={position}
            canGoBack={
              activeIndex !== undefined &&
              previousShowable(activeIndex) !== undefined
            }
            canAdvance={canAdvance}
            onNext={() => {
              try {
                // The button is disabled while the gate is shut; re-check in case the page changed since.
                if (!isStepDone(currentStep)) {
                  setCanAdvance(false);
                  return;
                }
                advance();
              } catch (error) {
                failRef.current(error);
              }
            }}
            onBack={() => {
              try {
                back();
              } catch (error) {
                failRef.current(error);
              }
            }}
            onExit={exit}
          />,
          driverApi.popoverContainer
        )
      : null;

  return {
    start,
    exit,
    isActive: !!run || !!title,
    currentStep,
    popover: titleScreen ?? popoverNode,
  };
}
